import { MODERATION_URL_PATH } from './constants';
import {
  DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH,
  IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION,
  IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION,
} from '../components/MagicApronAssistant/constants';

/* eslint-disable no-param-reassign */

/**
 * Waits for a ref property to become truthy
 * @param {Object} ref - React ref object
 * @param {string} key - Property key to wait for
 * @param {boolean} skip - Skip waiting if true
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise} Resolves when ref[key] becomes truthy
 */
export const waitForRefValue = (ref, key, skip = false, timeout = 10000) => {
  return new Promise((resolve, reject) => {
    if (ref.current[key] || skip) resolve();
    else if (ref.current.resolver) reject(new Error('Ref already has resolver'));
    else {
      const timeoutId = setTimeout(() => {
        ref.current.resolver = null;
        reject(new Error(`Ref failed to resolve after ${timeout}`));
      }, timeout);

      ref.current.resolver = () => {
        clearTimeout(timeoutId);
        resolve();
        ref.current.resolver = null;
      };
    }
  });
};

// Fisher-Yates shuffle algorithm
const shuffle = (arr) => {
  let endI = arr.length - 1;

  while (endI) {
    let randI = Math.floor(Math.random() * endI);
    // eslint-disable-next-line no-param-reassign
    [arr[endI], arr[randI]] = [arr[randI], arr[endI]];
    endI -= 1;
  }

  return arr;
};

export const getRandomElements = (list, items = 3) => shuffle([...list]).slice(0, items);

export const getContentModeration = (apiOnlineHost, imageFile, uploadContext) => {

  const moderationUrl = apiOnlineHost + MODERATION_URL_PATH;
  const formData = new FormData();
  formData.append('imageFile', imageFile);
  formData.append('context', JSON.stringify(uploadContext));

  return fetch(moderationUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
    credentials: 'include',
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`Moderation request failed with status ${res.status}`);
    }
    return res.json();
  }).then(({ imageId, isValid, storageLink, moderationStatus }) => ({
    imageId,
    isValid,
    storageLink,
    moderationStatus,
  }));
};

/**
 * The surface the assistant is running on, as one value.
 *
 * `isConsumerApp` is checked before `channel` because the consumer app reports
 * the mobile channel — reading channel first would report it as mobile web.
 * Matches how the two are already combined in checkGenerateQuestionsNeeded below.
 *
 * @param {Object} experienceContext - Fields off ExperienceContext.
 * @param {string} [experienceContext.channel] - 'desktop' or 'mobile'.
 * @param {boolean} [experienceContext.isConsumerApp] - True inside the THD app.
 * @returns {'app'|'mobile'|'desktop'} Defaults to desktop, as `channel` itself does.
 */
export const getDeviceType = ({ channel, isConsumerApp } = {}) => {
  if (isConsumerApp) return 'app';
  if (channel === 'mobile') return 'mobile';
  return 'desktop';
};

export const checkGenerateQuestionsNeeded = ({
  savedMessages,
  channel,
  isConsumerApp,
  isDynamicIsqMasterEnabled,
  isDynamicIsqDtFsEnabled,
  isDynamicIsqMwFsEnabled,
  isDynamicIsqAppFsEnabled,
  isDynamicIsqValidCustomerType
}) => {
  const isDesktop = channel === 'desktop';
  const isMobile = channel === 'mobile' && !isConsumerApp;
  const initialMessage = savedMessages.find((msg) => msg.isInitialMessage);

  const hasCustomerInteracted = savedMessages.length > 1;
  const isISQExistsForInitialMessage = initialMessage?.content?.relatedQuestions?.suggestedQuestions?.length > 0;

  if (isISQExistsForInitialMessage || hasCustomerInteracted) return false;

  // Fetch flags to determine if we should call generate questions
  const enableForDesktop = isDesktop && isDynamicIsqDtFsEnabled;
  const enableForMobile = isMobile && isDynamicIsqMwFsEnabled;
  const enableForApp = isConsumerApp && isDynamicIsqAppFsEnabled;
  const shouldCallGenerateQuestions = isDynamicIsqMasterEnabled
    && isDynamicIsqValidCustomerType
    && (enableForDesktop || enableForMobile || enableForApp);
  return shouldCallGenerateQuestions;
};

/**
 * Formats a millisecond timestamp into h:mmAM/PM format.
 * @param {number} timestamp
 * @returns {string}
 */
export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes}${ampm}`;
};

// Constructing an Intl.DateTimeFormat is expensive and formatDate can be called many
// times per render with repeat timestamps. Reusing one instance and caching results by
// calendar day turns repeat calls into cheap lookups instead of full re-formatting.
const dateFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const formattedDateCache = new Map();

export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const dayKey = (date.getFullYear() * 10000) + ((date.getMonth() + 1) * 100) + date.getDate();

  if (!formattedDateCache.has(dayKey)) {
    formattedDateCache.set(dayKey, dateFormatter.format(date));
  }

  return formattedDateCache.get(dayKey);
};

export const getImageDimensions = (aspectRatio, containerWidth = DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH) => {
  const maxWidth = containerWidth * IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION;
  const maxHeight = window.innerHeight * IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION;
  let width = maxWidth;
  let height = width / aspectRatio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  return { width: Math.round(width), height: Math.round(height) };
};